import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Edit, Phone, Mail, MapPin, Calendar, User, Tag, Archive, AlertTriangle, Eye, EyeOff, Copy, Plus, Trash2, RefreshCw, CalendarDays, Clock, Video, ListChecks, MessageCircle, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { fmtDate, statusColor, canDelete, CLIENT_SERVICES } from '../utils/helpers';
import { StatusBadge, Modal, FormField } from '../components/Table';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

function ClientMeetingsTab({ clientId }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/meetings/client/${clientId}`).then(r => setMeetings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  const STATUS_COLOR = {
    'Scheduled':   'bg-blue-100 text-blue-700',
    'Completed':   'bg-green-100 text-green-700',
    'Cancelled':   'bg-gray-100 text-gray-500',
    'Postponed':   'bg-orange-100 text-orange-700',
    'In Progress': 'bg-indigo-100 text-indigo-700',
  };

  function fmt12(time) {
    if (!time) return '';
    const [h, m] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${ampm}`;
  }

  if (loading) return <div className="py-8 text-center text-gray-400">Loading meetings...</div>;

  return (
    <div className="space-y-3 mt-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-700 text-sm">Meeting History ({meetings.length})</h3>
        <Link to={`/meetings?client_id=${clientId}`} className="text-xs text-[#1e3a5f] hover:underline">View in Meetings →</Link>
      </div>
      {meetings.length === 0 ? (
        <div className="card text-center py-8 text-gray-400">
          <CalendarDays size={32} className="mx-auto mb-2 text-gray-300" />
          <p>No meetings for this client.</p>
        </div>
      ) : (
        meetings.map(m => (
          <Link key={m.id} to={`/meetings/${m.id}`} className="card flex items-start gap-3 hover:shadow-md transition-shadow">
            <div className="flex-shrink-0 w-12 text-center">
              <div className="text-xs text-gray-400">{new Date(m.meeting_date + 'T00:00').toLocaleDateString('en-IN', { month: 'short' })}</div>
              <div className="text-2xl font-bold text-[#1e3a5f]">{new Date(m.meeting_date + 'T00:00').getDate()}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-800 text-sm">{m.title}</div>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[m.status] || 'bg-gray-100 text-gray-600'}`}>{m.status}</span>
                <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={11} />{fmt12(m.meeting_time)}</span>
                <span className="text-xs text-gray-400">{m.mode}</span>
                {m.pending_actions > 0 && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{m.pending_actions} actions pending</span>}
                {m.status === 'Completed' && !m.mom_sent && <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded border border-red-200">MOM pending</span>}
              </div>
            </div>
          </Link>
        ))
      )}
    </div>
  );
}

const WORK_TYPE_COLOR = {
  ITR: 'bg-blue-100 text-blue-700', GST: 'bg-orange-100 text-orange-700',
  TDS: 'bg-purple-100 text-purple-700', Audit: 'bg-red-100 text-red-700',
  ROC: 'bg-indigo-100 text-indigo-700', 'ESI-PF': 'bg-teal-100 text-teal-700',
  Accounts: 'bg-green-100 text-green-700',
};

function ClientChecklistsTab({ clientId }) {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/checklists/client/${clientId}`).then(r => setChecklists(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="py-8 text-center text-gray-400">Loading...</div>;

  return (
    <div className="space-y-3 mt-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-700 text-sm">Document Checklists ({checklists.length})</h3>
        <Link to="/checklists" className="text-xs text-[#1e3a5f] hover:underline">View All →</Link>
      </div>
      {checklists.length === 0 ? (
        <div className="card text-center py-8 text-gray-400">
          <ListChecks size={32} className="mx-auto mb-2 text-gray-300" />
          <p>No checklists for this client.</p>
          <Link to="/checklists" className="btn-primary text-xs px-4 py-1.5 mt-3 inline-flex items-center gap-1"><Plus size={12} /> Create Checklist</Link>
        </div>
      ) : (
        checklists.map(cl => {
          const pct = cl.total_items ? Math.round((cl.received_items / cl.total_items) * 100) : 0;
          return (
            <Link key={cl.id} to={`/checklists/${cl.id}`} className="card flex items-center gap-3 hover:shadow-md transition-shadow">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${WORK_TYPE_COLOR[cl.work_type] || 'bg-gray-100 text-gray-600'}`}>{cl.work_type}</span>
                  {cl.fy && <span className="text-xs text-gray-500">FY {cl.fy}</span>}
                  {cl.period && <span className="text-xs text-gray-400">{cl.period}</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cl.status === 'Completed' ? 'bg-green-100 text-green-700' : cl.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{cl.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-xs">
                    <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-[#1e3a5f]'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-gray-500">{cl.received_items}/{cl.total_items}</span>
                  {cl.pending_items > 0 && <span className="text-xs text-red-600 font-medium">{cl.pending_items} pending</span>}
                </div>
              </div>
              <ChevronRight size={16} className="text-gray-300" />
            </Link>
          );
        })
      )}
    </div>
  );
}

const PORTAL_TYPES = ['Income Tax (IT)', 'GST (GSTN)', 'TDS/TAN (TRACES)', 'MCA/ROC', 'ESI', 'PF (EPFO)', 'Banking', 'Other'];
const MEMBER_TYPES = ['Director', 'Designated Partner', 'Partner', 'Karta', 'Member', 'Trustee'];
const RECURRING_MODULES = [
  { key: 'gst', label: 'GST Returns', returnTypes: ['GSTR-1', 'GSTR-3B', 'GSTR-9', 'GSTR-9C'], dueDates: { 'GSTR-1': '11th of following month', 'GSTR-3B': '20th of following month', 'GSTR-9': '31 Dec (annual)', 'GSTR-9C': '31 Dec (annual)' } },
  { key: 'tds', label: 'TDS Returns', returnTypes: ['24Q', '26Q', '27Q', '27EQ'], dueDates: { '24Q': '31 Jul/Oct/Jan/May', '26Q': '31 Jul/Oct/Jan/May', '27Q': '15 Jul/Oct/Jan/May', '27EQ': '15 Jul/Oct/Jan/May' } },
  { key: 'itr', label: 'Income Tax (ITR)', returnTypes: ['Non-Audit', 'Audit'], dueDates: { 'Non-Audit': '31 July', 'Audit': '31 October' } },
  { key: 'esipf', label: 'ESI / PF', returnTypes: ['PF', 'ESI'], dueDates: { 'PF': '15th of following month', 'ESI': '15th of following month' } },
  { key: 'roc', label: 'ROC Compliance', returnTypes: ['MGT-7', 'AOC-4', 'DIR-3 KYC'], dueDates: { 'MGT-7': '60 days from AGM', 'AOC-4': '30 days from AGM', 'DIR-3 KYC': '30 Sep annually' } },
  { key: 'accounts', label: 'Accounts/Bookkeeping', returnTypes: [] },
];

function CredentialsTab({ clientId, isAdmin }) {
  const [creds, setCreds] = useState([]);
  const [showPwd, setShowPwd] = useState({});
  const [editPortal, setEditPortal] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => { api.get(`/credentials/${clientId}`).then(r => setCreds(r.data)).catch(() => {}); }, [clientId]);

  const save = async () => {
    try {
      await api.put(`/credentials/${clientId}/${encodeURIComponent(editPortal)}`, form);
      setCreds(prev => {
        const idx = prev.findIndex(c => c.portal === editPortal);
        if (idx >= 0) { const n = [...prev]; n[idx] = { ...n[idx], ...form }; return n; }
        return [...prev, { portal: editPortal, ...form }];
      });
      toast.success('Saved'); setEditPortal(null);
    } catch { toast.error('Failed'); }
  };

  const openEdit = (portal) => {
    const c = creds.find(x => x.portal === portal) || {};
    setForm({ user_id_field: c.user_id_field || '', password: '***', notes: c.notes || '' });
    setEditPortal(portal);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400">Click Edit to update portal credentials. Passwords are encrypted at rest.</p>
      {PORTAL_TYPES.map(portal => {
        const c = creds.find(x => x.portal === portal);
        return (
          <div key={portal} className="card">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-gray-700">{portal}</span>
              <button onClick={() => openEdit(portal)} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</button>
            </div>
            {c && (
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-xs text-gray-400">User ID / Login: </span><span className="font-mono">{c.user_id_field || '—'}</span></div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">Password: </span>
                  <span className="font-mono text-sm">{isAdmin && showPwd[portal] ? c.password_plain || '***' : '***'}</span>
                  {isAdmin && <button onClick={() => setShowPwd(p => ({...p, [portal]: !p[portal]}))} className="text-gray-400 hover:text-gray-700">{showPwd[portal] ? <EyeOff size={13} /> : <Eye size={13} />}</button>}
                  {isAdmin && c.password_plain && <button onClick={() => { navigator.clipboard.writeText(c.password_plain); toast.success('Copied'); }} className="text-gray-400 hover:text-[#f0a500]"><Copy size={13} /></button>}
                </div>
                {c.notes && <div className="col-span-2 text-xs text-gray-500">{c.notes}</div>}
              </div>
            )}
          </div>
        );
      })}
      {editPortal && (
        <Modal title={`Credentials — ${editPortal}`} onClose={() => setEditPortal(null)}>
          <div className="space-y-3">
            <FormField label="User ID / Login"><input value={form.user_id_field||''} onChange={e => setForm(f => ({...f, user_id_field: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Password">{isAdmin ? <input type="text" value={form.password||''} onChange={e => setForm(f => ({...f, password: e.target.value}))} className="input-field font-mono" placeholder="Leave *** to keep existing" /> : <span className="text-xs text-gray-400">Not visible for your role</span>}</FormField>
            <FormField label="Notes"><textarea value={form.notes||''} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="input-field" rows={2} /></FormField>
            <div className="flex gap-3"><button onClick={save} className="btn-primary flex-1">Save</button><button onClick={() => setEditPortal(null)} className="btn-secondary flex-1">Cancel</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function MembersTab({ clientId }) {
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ member_type: 'Director', is_active: 1 });

  const load = () => api.get(`/members/${clientId}`).then(r => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);

  const save = async e => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/members/${editing.id}`, form);
      else await api.post(`/members/${clientId}`, form);
      toast.success('Saved'); setShowModal(false); setEditing(null); load();
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete member?')) return;
    await api.delete(`/members/${id}`); load();
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => { setEditing(null); setForm({ member_type: 'Director', is_active: 1 }); setShowModal(true); }} className="btn-primary flex items-center gap-2 text-sm"><Plus size={15} /> Add Member</button>
      </div>
      {members.length === 0 ? <p className="text-gray-400 text-sm text-center py-8">No members added yet.</p> : (
        <div className="space-y-3">
          {members.map(m => (
            <div key={m.id} className={`card ${!m.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-gray-800">{m.name} <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded ml-1">{m.member_type}</span></div>
                  {m.designation && <div className="text-xs text-gray-500">{m.designation}</div>}
                  <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                    {m.pan && <span>PAN: {m.pan}</span>}
                    {m.din && <span>DIN: {m.din}</span>}
                    {m.mobile && <span>Mobile: {m.mobile}</span>}
                    {m.email && <span>Email: {m.email}</span>}
                    {m.date_of_appointment && <span>Appointment: {fmtDate(m.date_of_appointment)}</span>}
                    {m.date_of_resignation && <span className="text-red-500">Resigned: {fmtDate(m.date_of_resignation)}</span>}
                    {m.capital_percentage && <span>Capital: {m.capital_percentage}%</span>}
                    {m.profit_sharing_ratio && <span>Profit: {m.profit_sharing_ratio}%</span>}
                    {m.dsc_expiry_date && <span>DSC Expiry: {fmtDate(m.dsc_expiry_date)}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setEditing(m); setForm(m); setShowModal(true); }} className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</button>
                  <button onClick={() => remove(m.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showModal && (
        <Modal title={editing ? 'Edit Member' : 'Add Member'} onClose={() => { setShowModal(false); setEditing(null); }} size="lg">
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <FormField label="Member Type"><select value={form.member_type||'Director'} onChange={e => setForm(f => ({...f, member_type: e.target.value}))} className="input-field">{MEMBER_TYPES.map(t => <option key={t}>{t}</option>)}</select></FormField>
            <FormField label="Full Name" required><input required value={form.name||''} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Designation"><input value={form.designation||''} onChange={e => setForm(f => ({...f, designation: e.target.value}))} className="input-field" /></FormField>
            <FormField label="DIN / DPIN"><input value={form.din||''} onChange={e => setForm(f => ({...f, din: e.target.value}))} className="input-field" /></FormField>
            <FormField label="PAN"><input value={form.pan||''} onChange={e => setForm(f => ({...f, pan: e.target.value.toUpperCase()}))} className="input-field" maxLength={10} /></FormField>
            <FormField label="Aadhaar"><input value={form.aadhaar||''} onChange={e => setForm(f => ({...f, aadhaar: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Mobile"><input value={form.mobile||''} onChange={e => setForm(f => ({...f, mobile: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Email"><input type="email" value={form.email||''} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Date of Appointment"><input type="date" value={form.date_of_appointment||''} onChange={e => setForm(f => ({...f, date_of_appointment: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Date of Resignation"><input type="date" value={form.date_of_resignation||''} onChange={e => setForm(f => ({...f, date_of_resignation: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Capital %"><input type="number" step="0.01" value={form.capital_percentage||''} onChange={e => setForm(f => ({...f, capital_percentage: e.target.value}))} className="input-field" /></FormField>
            <FormField label="Profit Sharing %"><input type="number" step="0.01" value={form.profit_sharing_ratio||''} onChange={e => setForm(f => ({...f, profit_sharing_ratio: e.target.value}))} className="input-field" /></FormField>
            <FormField label="DSC Expiry Date"><input type="date" value={form.dsc_expiry_date||''} onChange={e => setForm(f => ({...f, dsc_expiry_date: e.target.value}))} className="input-field" /></FormField>
            <FormField label="KYC Status"><select value={form.kyc_status||'Pending'} onChange={e => setForm(f => ({...f, kyc_status: e.target.value}))} className="input-field"><option>Pending</option><option>Completed</option><option>Expired</option></select></FormField>
            <FormField label="Active"><select value={form.is_active} onChange={e => setForm(f => ({...f, is_active: parseInt(e.target.value)}))} className="input-field"><option value={1}>Yes</option><option value={0}>No</option></select></FormField>
            <FormField label="Notes" className="col-span-2"><textarea value={form.notes||''} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="input-field" rows={2} /></FormField>
            <div className="col-span-2 flex gap-3"><button type="submit" className="btn-primary flex-1">Save</button><button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn-secondary flex-1">Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function RecurringTab({ clientId }) {
  const [setups, setSetups] = useState([]);
  const [staff, setStaff] = useState([]);
  const FY_OPTIONS_LOCAL = ['2024-25', '2025-26', '2026-27'];

  const load = () => api.get(`/recurring/client/${clientId}`).then(r => setSetups(r.data)).catch(() => {});
  useEffect(() => { load(); api.get('/auth/users').then(r => setStaff(r.data)).catch(() => {}); }, [clientId]);

  const update = async (mod, field, value) => {
    const existing = setups.find(s => s.module === mod) || { module: mod, is_active: 1, frequency: 'monthly', return_types: [], start_fy: '2025-26' };
    const updated = { ...existing, [field]: value };
    await api.put(`/recurring/client/${clientId}/${mod}`, updated).catch(() => toast.error('Failed'));
    load();
  };

  const trigger = async () => {
    const r = await api.post('/recurring/trigger').catch(() => ({ data: { created: 0 } }));
    toast.success(`${r.data.created} records created`);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-xs text-gray-400">Configure periodicity for each service. Enable auto-create to generate records automatically each period.</p>
        <button onClick={trigger} className="btn-secondary flex items-center gap-2 text-sm"><RefreshCw size={14} /> Trigger Now</button>
      </div>
      <div className="space-y-3">
        {RECURRING_MODULES.map(mod => {
          const setup = setups.find(s => s.module === mod.key) || {};
          const isActive = !!setup.is_active;
          return (
            <div key={mod.key} className="card">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={isActive} onChange={e => update(mod.key, 'is_active', e.target.checked ? 1 : 0)} className="rounded" />
                  <span className="font-medium text-sm text-gray-700">{mod.label}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <select value={setup.frequency || 'monthly'} onChange={e => update(mod.key, 'frequency', e.target.value)} className="input-field text-xs py-1 w-28">
                    <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
                  </select>
                  <select value={setup.start_fy || '2025-26'} onChange={e => update(mod.key, 'start_fy', e.target.value)} className="input-field text-xs py-1 w-24">
                    {FY_OPTIONS_LOCAL.map(f => <option key={f}>{f}</option>)}
                  </select>
                  <select value={setup.auto_assign_to || ''} onChange={e => update(mod.key, 'auto_assign_to', e.target.value || null)} className="input-field text-xs py-1 w-36">
                    <option value="">No assignment</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              {mod.returnTypes.length > 0 && (
                <div className="mt-2 space-y-1">
                  {mod.returnTypes.map(rt => (
                    <div key={rt} className="flex items-center gap-3">
                      <button type="button"
                        onClick={() => { const cur = setup.return_types||[]; update(mod.key, 'return_types', cur.includes(rt) ? cur.filter(x => x !== rt) : [...cur, rt]); }}
                        className={`px-2.5 py-0.5 text-xs rounded border transition-colors ${(setup.return_types||[]).includes(rt) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'text-gray-500 border-gray-300 hover:border-[#1e3a5f]'}`}>{rt}</button>
                      {mod.dueDates?.[rt] && <span className="text-xs text-gray-400">Due: {mod.dueDates[rt]}</span>}
                    </div>
                  ))}
                </div>
              )}
              {setup.last_created_period && <p className="text-xs text-gray-400 mt-2">Last created: {setup.last_created_period}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SERVICES = CLIENT_SERVICES;
const ENTITY_TYPES = ['Individual', 'HUF', 'Firm', 'LLP', 'Pvt Ltd', 'Public Ltd', 'Trust', 'AOP'];

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <span className="text-xs text-gray-400 w-28 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-700 font-medium">{value}</span>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [staff, setStaff] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [archiveModal, setArchiveModal] = useState(false);
  const [archiveCheck, setArchiveCheck] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [permDeleteModal, setPermDeleteModal] = useState(false);
  const [permDeleteConfirm, setPermDeleteConfirm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const isAdmin = ['partner','senior_staff','super_admin','admin'].includes(user?.role);

  useEffect(() => {
    api.get(`/clients/${id}`).then(r => { setClient(r.data); setForm(r.data); });
    api.get('/auth/users').then(r => setStaff(r.data));
    api.get('/tasks', { params: { client_id: id } }).then(r => setTasks(r.data.filter(t => t.status !== 'Done')));
    api.get('/billing', { params: { client_id: id } }).then(r => setInvoices(r.data.filter(i => ['Sent','Overdue','Partial'].includes(i.status))));
  }, [id]);

  const handleSave = async () => {
    try {
      await api.put(`/clients/${id}`, { ...form, gstins: form.gstins || [] });
      setClient(form);
      setEditing(false);
      toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed to save'); }
  };

  const toggleService = svc => {
    setForm(f => ({ ...f, services: f.services?.includes(svc) ? f.services.filter(s => s !== svc) : [...(f.services || []), svc] }));
  };

  const handleArchiveClick = async () => {
    const r = await api.get(`/clients/${id}/archive-check`);
    setArchiveCheck(r.data);
    setArchiveModal(true);
  };

  const handleArchive = async (force = false) => {
    try {
      await api.post(`/clients/${id}/archive`, { reason: archiveReason, force });
      toast.success('Client archived');
      navigate('/clients');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Archive failed');
    }
  };

  const handlePermDelete = async () => {
    if (permDeleteConfirm !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    try {
      await api.delete(`/clients/${id}/permanent`);
      toast.success('Client permanently deleted');
      navigate('/clients');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Delete failed');
    }
  };

  if (!client) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-4">
        <Link to="/clients" className="text-gray-400 hover:text-[#1e3a5f]"><ArrowLeft size={20} /></Link>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-[#1e3a5f]">{client.name}</h1>
            {client.client_code && <span className="font-mono text-xs font-bold text-[#1e3a5f] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">{client.client_code}</span>}
          </div>
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{client.entity_type}</span>
        </div>
        <StatusBadge status={client.status} />
        <button onClick={() => editing ? handleSave() : setEditing(true)} className="btn-primary flex items-center gap-2">
          <Edit size={15} /> {editing ? 'Save Changes' : 'Edit'}
        </button>
        {editing && <button onClick={() => { setEditing(false); setForm(client); }} className="btn-secondary">Cancel</button>}
        {canDelete(user) && !client.archived_at && (
          <button onClick={handleArchiveClick} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-orange-200 text-orange-600 text-sm hover:bg-orange-50 transition-colors">
            <Archive size={15} /> Archive
          </button>
        )}
        {isAdmin && (
          <button onClick={() => { setPermDeleteConfirm(''); setPermDeleteModal(true); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-700 hover:bg-red-800 text-white text-sm transition-colors">
            <Trash2 size={15} /> Delete Permanently
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-200 flex-wrap">
        {[['overview', 'Overview'], ['credentials', 'Credentials'], ['members', 'Members'], ['recurring', 'Periodicity'], ['meetings', 'Meetings'], ['checklists', 'Checklists']].map(([t, l]) => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === t ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
        <Link to={`/clients/${id}/work-ledger`}
          className="px-4 py-2 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors">Work &amp; Billing →</Link>
      </div>

      {activeTab === 'credentials' && <CredentialsTab clientId={id} isAdmin={isAdmin} />}
      {activeTab === 'members' && <MembersTab clientId={id} />}
      {activeTab === 'recurring' && <RecurringTab clientId={id} />}
      {activeTab === 'meetings' && <ClientMeetingsTab clientId={id} />}
      {activeTab === 'checklists' && <ClientChecklistsTab clientId={id} />}

      {activeTab === 'overview' && <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Main info */}
        <div className="card md:col-span-2 space-y-4">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Client Information</h3>
          {editing ? (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500">Client Code *</label><input value={form.client_code||''} onChange={e => setForm(f => ({...f, client_code: e.target.value}))} className="input-field mt-1 font-mono" placeholder="e.g. KUA-001" /></div>
              <div><label className="text-xs text-gray-500">Name</label><input value={form.name||''} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="input-field mt-1" /></div>
              <div><label className="text-xs text-gray-500">PAN</label><input value={form.pan||''} onChange={e => setForm(f => ({...f, pan: e.target.value.toUpperCase()}))} className="input-field mt-1" maxLength={10} /></div>
              <div><label className="text-xs text-gray-500">Aadhaar</label><input value={form.aadhaar||''} onChange={e => setForm(f => ({...f, aadhaar: e.target.value}))} className="input-field mt-1" /></div>
              <div><label className="text-xs text-gray-500">TAN</label><input value={form.tan||''} onChange={e => setForm(f => ({...f, tan: e.target.value.toUpperCase()}))} className="input-field mt-1" /></div>
              <div><label className="text-xs text-gray-500">CIN</label><input value={form.cin||''} onChange={e => setForm(f => ({...f, cin: e.target.value}))} className="input-field mt-1" /></div>
              <div>
                <label className="text-xs text-gray-500">Mobile</label>
                <input value={form.mobile||''} onChange={e => {
                  const val = e.target.value;
                  setForm(f => ({ ...f, mobile: val, ...(f.whatsapp === f.mobile ? { whatsapp: val } : {}) }));
                }} className="input-field mt-1" />
              </div>
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-2">
                  WhatsApp
                  <span className="flex items-center gap-1 font-normal">
                    <input type="checkbox" id="wa-same" checked={form.whatsapp === form.mobile}
                      onChange={e => setForm(f => ({ ...f, whatsapp: e.target.checked ? f.mobile : '' }))}
                      className="w-3 h-3 accent-[#1e3a5f]" />
                    <label htmlFor="wa-same" className="cursor-pointer text-gray-400">Same as Mobile</label>
                  </span>
                </label>
                <input value={form.whatsapp||''} onChange={e => setForm(f => ({...f, whatsapp: e.target.value}))}
                  className="input-field mt-1" placeholder="WhatsApp number" disabled={form.whatsapp === form.mobile && !!form.mobile} />
              </div>
              <div><label className="text-xs text-gray-500">Email</label><input type="email" value={form.email||''} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="input-field mt-1" /></div>
              <div><label className="text-xs text-gray-500">Status</label>
                <select value={form.status||'Active'} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="input-field mt-1">
                  {['Active','Inactive','Prospect'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-gray-500">Assigned Staff</label>
                <select value={form.assigned_staff_id||''} onChange={e => setForm(f => ({...f, assigned_staff_id: e.target.value}))} className="input-field mt-1">
                  <option value="">Select</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-gray-500">Client Since</label><input type="date" value={form.client_since||''} onChange={e => setForm(f => ({...f, client_since: e.target.value}))} className="input-field mt-1" /></div>
              <div className="col-span-2"><label className="text-xs text-gray-500">Notes</label><textarea value={form.notes||''} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="input-field mt-1" rows={2} /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-y-3 gap-x-6">
              <InfoRow label="PAN" value={client.pan} />
              <InfoRow label="Aadhaar" value={client.aadhaar} />
              <InfoRow label="TAN" value={client.tan} />
              <InfoRow label="CIN / LLPIN" value={client.cin || client.llpin} />
              <InfoRow label="Mobile" value={client.mobile} />
              <InfoRow label="Email" value={client.email} />
              <InfoRow label="WhatsApp" value={client.whatsapp} />
              <InfoRow label="Assigned To" value={client.staff_name} />
              <InfoRow label="Client Since" value={fmtDate(client.client_since)} />
              <InfoRow label="Entity Type" value={client.entity_type} />
              <div className="col-span-2"><InfoRow label="Address" value={client.address_registered} /></div>
              {client.notes && <div className="col-span-2 p-3 bg-yellow-50 rounded-lg text-sm text-gray-600">{client.notes}</div>}
            </div>
          )}

          {/* Services */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Services</h4>
            {editing ? (
              <div className="flex flex-wrap gap-2">
                {SERVICES.map(svc => (
                  <button key={svc} type="button" onClick={() => toggleService(svc)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${form.services?.includes(svc) ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'text-gray-600 border-gray-300'}`}>
                    {svc}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {client.services?.map(svc => <span key={svc} className="px-2 py-0.5 bg-[#1e3a5f] text-white text-xs rounded-full">{svc}</span>)}
              </div>
            )}
          </div>

          {/* GSTINs */}
          {client.gstins?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">GSTIN(s)</h4>
              <div className="space-y-1">
                {client.gstins.map(g => <div key={g.id} className="flex gap-3 text-sm"><span className="font-mono font-medium text-gray-700">{g.gstin}</span><span className="text-gray-400">{g.state}</span></div>)}
              </div>
            </div>
          )}
        </div>

        {/* Right panel */}
        <div className="space-y-4">
          {/* Pending tasks */}
          <div className="card">
            <h4 className="font-semibold text-gray-700 text-sm mb-3">Pending Tasks ({tasks.length})</h4>
            {tasks.length === 0 ? <p className="text-gray-400 text-xs">No pending tasks</p> : (
              <div className="space-y-2">
                {tasks.slice(0, 5).map(t => (
                  <div key={t.id} className="flex items-start gap-2 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${t.priority === 'High' ? 'bg-red-500' : t.priority === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'}`} />
                    <span className="text-gray-700">{t.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Outstanding invoices */}
          {invoices.length > 0 && (
            <div className="card">
              <h4 className="font-semibold text-gray-700 text-sm mb-3">Outstanding Invoices</h4>
              <div className="space-y-2">
                {invoices.map(inv => (
                  <div key={inv.id} className="flex justify-between text-xs">
                    <span className="text-gray-600">{inv.invoice_number}</span>
                    <span className="font-semibold text-red-600">₹{(inv.total - inv.paid_amount).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick links */}
          <div className="card">
            <h4 className="font-semibold text-gray-700 text-sm mb-3">Quick Access</h4>
            <div className="space-y-1">
              {[
                { label: 'Income Tax', to: `/income-tax?client=${id}` },
                { label: 'GST Returns', to: `/gst?client=${id}` },
                { label: 'TDS Returns', to: `/tds?client=${id}` },
                { label: 'Documents', to: `/documents?client=${id}` },
                { label: 'Billing', to: `/billing?client=${id}` },
              ].map(l => <Link key={l.to} to={l.to} className="block text-xs text-[#1e3a5f] hover:text-[#f0a500] py-1 border-b border-gray-50 last:border-0">{l.label} →</Link>)}
            </div>
          </div>
        </div>
      </div>}

      {/* Permanent delete modal */}
      {permDeleteModal && (
        <Modal title="⚠️ Permanent Deletion Warning" onClose={() => setPermDeleteModal(false)} size="md">
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-300 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-700">This will COMPLETELY and PERMANENTLY delete <strong>{client.name}</strong> and ALL related data:</p>
              </div>
              <ul className="text-xs text-red-600 space-y-0.5 ml-6 list-disc">
                <li>All ITR/TDS/GST/Audit/ROC/ESI records</li>
                <li>All tasks, documents, invoices</li>
                <li>All credentials and member details</li>
                <li>All ledger entries and recurring work</li>
              </ul>
              <p className="text-xs font-bold text-red-700 mt-3">THIS CANNOT BE UNDONE. There is no restore option for permanent deletion.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type <strong className="text-red-600">DELETE</strong> to confirm</label>
              <input value={permDeleteConfirm} onChange={e => setPermDeleteConfirm(e.target.value)}
                className="input-field font-mono tracking-widest" placeholder="DELETE" autoComplete="off" />
            </div>
            <div className="flex gap-3">
              <button onClick={handlePermDelete} disabled={permDeleteConfirm !== 'DELETE'}
                className="flex-1 bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white py-2 rounded-lg font-medium text-sm transition-colors">
                Confirm Permanent Delete
              </button>
              <button onClick={() => setPermDeleteModal(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Archive modal */}
      {archiveModal && archiveCheck && (
        <Modal title="Archive Client" onClose={() => setArchiveModal(false)} size="md">
          <div className="space-y-4">
            {(!archiveCheck.canArchive) && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={18} className="text-orange-600" />
                  <span className="font-semibold text-orange-700">Warning — Pending Items</span>
                </div>
                {archiveCheck.unpaidInvoices > 0 && (
                  <div className="text-sm text-orange-700 mb-1">
                    <strong>{archiveCheck.unpaidInvoices} unpaid invoice(s)</strong> — total outstanding amount pending
                  </div>
                )}
                {archiveCheck.activeTasks > 0 && (
                  <div className="text-sm text-orange-700">
                    <strong>{archiveCheck.activeTasks} active task(s)</strong> — these will remain but be flagged
                  </div>
                )}
                <p className="text-xs text-orange-600 mt-2">You can still archive by force — the data will be preserved.</p>
              </div>
            )}
            {archiveCheck.canArchive && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                ✓ No pending items. Safe to archive.
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for archiving</label>
              <textarea value={archiveReason} onChange={e => setArchiveReason(e.target.value)} className="input-field" rows={2} placeholder="e.g. Client discontinued services, duplicate entry..." />
            </div>
            <div className="flex gap-3">
              {archiveCheck.canArchive ? (
                <button onClick={() => handleArchive(false)} className="btn-primary flex-1">Archive Client</button>
              ) : (
                <button onClick={() => handleArchive(true)} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg font-medium text-sm transition-colors">Archive Anyway (Force)</button>
              )}
              <button onClick={() => setArchiveModal(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
